// Garage measuring diagram — faithful line-art recreation of the client's
// reference: one-point perspective from inside the garage towards the door.
// Clearly wider-than-tall room (garage proportions). Red arrowed dims + live value chips.
const Dim = ({ k, active, onHover, x1, y1, x2, y2, lx, ly, label }) => {
  const X1 = Number(x1);
  const Y1 = Number(y1);
  const X2 = Number(x2);
  const Y2 = Number(y2);
  const dx = X2 - X1;
  const dy = Y2 - Y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy;
  const ny = ux;
  const ah = 10;
  const aw = 5.5;
  const endTip = `${X2 + ux * ah},${Y2 + uy * ah}`;
  const endB1 = `${X2 + nx * aw},${Y2 + ny * aw}`;
  const endB2 = `${X2 - nx * aw},${Y2 - ny * aw}`;
  const startTip = `${X1 - ux * ah},${Y1 - uy * ah}`;
  const startB1 = `${X1 + nx * aw},${Y1 + ny * aw}`;
  const startB2 = `${X1 - nx * aw},${Y1 - ny * aw}`;
  return (
    <g
      data-dim={k}
      className={`dd-dim ${active === k ? 'dim-active' : ''}`}
      onMouseEnter={() => onHover && onHover(k)}
      onMouseLeave={() => onHover && onHover(null)}
    >
      <line x1={X1} y1={Y1} x2={X2} y2={Y2} className="dd-dim-hit" />
      <line x1={X1} y1={Y1} x2={X2} y2={Y2} />
      <polygon points={`${endTip} ${endB1} ${endB2}`} />
      <polygon points={`${startTip} ${startB1} ${startB2}`} />
      <text x={lx} y={ly}>{label}</text>
    </g>
  );
};

const VALUE_POS = {
  adancime: { cx: 673, cy: 118 },
  lintel: { cx: 434, cy: 208 },
  inaltime_gol: { cx: 498, cy: 388 },
  latime_gol: { cx: 746, cy: 434 },
  spatiu_stanga: { cx: 241, cy: 322 },
  spatiu_dreapta: { cx: 938, cy: 322 },
};

export const GarageDiagram = ({ active, onHover, caption, values = {} }) => (
  <svg viewBox="0 0 1180 735" data-testid="garage-diagram-svg" className="w-full h-auto" role="img" aria-label="Garage measuring diagram">
    {/* structural line-art (room clearly wider than tall) */}
    <g className="dd-room-line">
      {/* ceiling corners */}
      <line x1="9" y1="18" x2="180" y2="168" />
      <line x1="1171" y1="18" x2="1000" y2="168" />
      {/* floor corners */}
      <line x1="9" y1="718" x2="180" y2="560" />
      <line x1="1171" y1="718" x2="1000" y2="560" />
      {/* back wall */}
      <rect x="180" y="168" width="819" height="392" />
      {/* recessed door frame (down to the floor, no threshold) */}
      <rect x="267" y="206" width="647" height="354" />
      <line x1="267" y1="206" x2="301" y2="228" />
      <line x1="913" y1="206" x2="879" y2="228" />
      {/* empty door (gol) reaching the floor */}
      <rect x="301" y="228" width="578" height="332" />
    </g>

    {/* floor tile grid */}
    <g className="dd-grid">
      <line x1="138" y1="600" x2="1042" y2="600" />
      <line x1="104" y1="631" x2="1076" y2="631" />
      <line x1="70" y1="663" x2="1110" y2="663" />
      <line x1="35" y1="694" x2="1145" y2="694" />
      <line x1="303" y1="560" x2="184" y2="718" />
      <line x1="426" y1="560" x2="358" y2="718" />
      <line x1="590" y1="560" x2="590" y2="718" />
      <line x1="754" y1="560" x2="822" y2="718" />
      <line x1="877" y1="560" x2="996" y2="718" />
    </g>

    {/* dimensions (red, arrowed) */}
    <Dim k="adancime" active={active} onHover={onHover} x1="717" y1="135" x2="809" y2="68" lx="658" ly="100" label="DG" />
    <Dim k="lintel" active={active} onHover={onHover} x1="477" y1="142" x2="477" y2="228" lx="427" ly="190" label="HG" />
    <Dim k="inaltime_gol" active={active} onHover={onHover} x1="540" y1="232" x2="540" y2="558" lx="505" ly="368" label="H" />
    <Dim k="latime_gol" active={active} onHover={onHover} x1="304" y1="460" x2="876" y2="460" lx="795" ly="448" label="L" />
    <Dim k="spatiu_stanga" active={active} onHover={onHover} x1="180" y1="300" x2="301" y2="300" lx="205" ly="290" label="S" />
    <Dim k="spatiu_dreapta" active={active} onHover={onHover} x1="879" y1="300" x2="1000" y2="300" lx="972" ly="290" label="D" />

    {/* live entered values (chips next to each dimension) */}
    <g pointerEvents="none">
      {Object.entries(VALUE_POS).map(([k, { cx, cy }]) => {
        const v = values[k];
        if (!v || String(v).trim() === '') return null;
        const w = String(v).length * 11 + 20;
        return (
          <g key={k} data-testid={`dim-value-${k}`} className={`dd-dim-value ${active === k ? 'dim-active' : ''}`}>
            <rect x={cx - w / 2} y={cy - 11} width={w} height="22" rx="11" />
            <text x={cx} y={cy + 5} textAnchor="middle">{v}</text>
          </g>
        );
      })}
    </g>

    {/* caption */}
    <text x="590" y="700" textAnchor="middle" className="dd-caption">{caption}</text>
  </svg>
);
